import React, { FC } from 'react'
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { TransactionStatusEnum } from '@sparling/financial-common'
import { Loader } from '../../Loader'
import { RiskLevel } from '../../../pages/Dashboard/Trading/Staking/StakingBaskets/StakingBasketsTableLists'
import { Status } from '../../../pages/Dashboard/Trading/TreasuryManagment/TreasurySections/SettelmentAddresses/SettelmentAddressesTableLists'
import { IColumns, IDataItem } from './TableTypes'
import { GreenButton } from '../Buttons/GreenButton'
import { GrayButton } from '../Buttons/GrayButton'
import { icons } from '../../../constants/icons'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    margin: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    paddingBottom: ({ pb }: TableProps) => pb || theme.spacing(4),
    '& thead tr': {
      height: '50px',
    },
    '& th': {
      color: theme.palette.primary.light,
      opacity: '0.6',
      fontWeight: 400,
      fontSize: 12,
      borderColor: 'rgba(233,236,239,0.38)',
      whiteSpace: 'nowrap',
      lineHeight: 'inherit',
      paddingTop: 24,
      paddingBottom: 18,
      paddingLeft: 6,
      paddingRight: 6,
    },
    '& tr': {
      height: '35px',
      verticalAlign: 'baseline',
      '&:nth-child(even)': {
        backgroundColor: 'rgba(216,216,216,0.05)',
      },
    },
    '& td': {
      color: theme.palette.primary.light,
      border: 'none',
      padding: '10px 6px',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      '& div': {
        lineHeight: 1.5,
      },
      '&:last-child': {
        width: ({ widthLastTd }: TableProps) => widthLastTd,
        padding: '3px 6px',
      },

      '& .grey': {
        color: '#A5A5A5',
      },
      '& .green': {
        color: '#51E5A5',
      },
      '& .red': {
        color: '#FF4A4A',
      },
      '& .orange': {
        color: '#F8BB4D',
      },
      '& .coralpink': {
        color: '#ff6f5b',
      },
    },
  },
  container: {
    maxHeight: '1000px',
  },
  pagination: {
    marginTop: ({ paginationMT }: TableProps) => paginationMT || '6px',
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    '& .MuiPaginationItem-root': {
      color: '#A9AAAD',
      fontSize: '0.75rem',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    '& .MuiPaginationItem-page.Mui-selected': {
      color: '#fff !important',
      textAlign: 'center',
      backgroundColor: 'rgba(104, 91, 198, 0.8)',
      border: '1px solid rgb(104, 91, 198)',
      borderRadius: '4px',
    },
    '& .MuiPagination-ul': {
      flexWrap: 'unset',
    },
  },
  editIcon: {
    color: theme.palette.secondary.light,
    padding: '5px 10px',
    '& span': {
      fontSize: 20,
    },
  },
  viewIcon: {
    color: '#D2D3D4',
    padding: '5px 10px',
    '& span': {
      fontSize: 20,
    },
  },
  deleteIcon: {
    color: '#D56354',
    padding: '5px 10px',
    '& span': {
      fontSize: 20,
    },
  },
  infoIcon: {
    color: '#a8a9ac',
  },
  oneButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  twoButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  total: {
    color: theme.palette.primary.light,
    paddingTop: theme.spacing(2),
    '& p': {
      lineHeight: 2,
    },
    lineHeight: theme.spacing(8),
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& :first-child': {
      paddingRight: theme.spacing(2),
      opacity: 0.8,
    },
  },
  viewAll: {
    justifyContent: 'center',
    paddingTop: theme.spacing(1),
  },
  notFound: {
    backgroundColor: 'transparent !important',
    '& td': {
      paddingTop: '32px !important',
      paddingBottom: '32px !important',
    },
  },
}))

interface TableProps {
  columns: IColumns[]
  data: IDataItem[]
  loadData?: (value: number) => void
  page?: string | number
  getId?: (value: number | string | undefined) => void
  // eslint-disable-next-line
  getData?: (value: any) => void
  // eslint-disable-next-line
  onUnstake?: (value: any) => void
  pb?: number | string
  paginationMT?: number | string
  widthLastTd?: number | string
  viewAll?: boolean
  // eslint-disable-next-line
  totalFields?: any
  viewAllLink?: string
  textButton?: string
  loading?: boolean
  params?: {
    currentPage: number
    itemsPerPage: number
    itemCount: number
    totalItems: number
    totalPages: number
  }
}

export const TableSmall: FC<TableProps> = ({
  columns,
  data,
  loadData,
  getId,
  getData,
  onUnstake,
  params,
  pb,
  paginationMT,
  widthLastTd,
  viewAll,
  totalFields,
  viewAllLink,
  textButton,
  loading,
}) => {
  const classes = useStyles({ columns, data, loadData, getId, params, pb, paginationMT, widthLastTd, textButton })

  // eslint-disable-next-line
  const paginate = (event: any, pageNumber: any) => {
    // eslint-disable-next-line no-unused-expressions
    loadData && loadData(pageNumber)
  }

  const setColor = (index: number, row: IDataItem) => {
    let color = ''
    switch (true) {
      case index === 3 && row.riskLevel === RiskLevel.High:
        color = 'red'
        break
      case (index === 3 && row.riskLevel === RiskLevel.Medium) ||
        (index === 3 && row.statusSA === Status.Pending) ||
        // (index === 3 && row.status === TransactionStatusEnum.SIGNED && row.type === TransactionTypeEnum.DEPOSIT) ||
        // (index === 3 && row.status === TransactionStatusEnum.ACTION_NEEDED) ||
        (index === 3 && row.status === TransactionStatusEnum.REJECTED) ||
        // (index === 3 && row.status === TransactionStatusEnum.NEW) ||
        (index === 3 && row.status === TransactionStatusEnum.INITIATED) ||
        // (index === 3 && row.status === TransactionStatusEnum.UNDER_REVIEW) ||
        (index === 3 && row.status === TransactionStatusEnum.REJECTED):
        // (index === 3 && row.status === TransactionStatusEnum.APPROVED):
        color = 'orange'
        break
      case (index === 3 && row.riskLevel === RiskLevel.Low) ||
        (index === 3 && row.statusSA === Status.Approved) ||
        (index === 3 && row.status === TransactionStatusEnum.COMPLETED) ||
        // (index === 3 && row.status === TransactionStatusEnum.SIGNED && row.type === TransactionTypeEnum.WITHDRAWAL) ||
        (index === 2 && !!row.ask):
        color = 'green'
        break
      case index === 1 && !!row.bid:
        color = 'coralpink'
        break
      default:
        color = '#FFFFFF'
    }
    return color
  }

  return (
    <>
      {loading && <Loader />}

      <Paper className={classes.root}>
        <TableContainer>
          <Table aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column, idx) => (
                  <TableCell key={idx}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data && data.length > 0 ? (
                data.map((row, idx) => {
                  return (
                    <TableRow hover role="checkbox" key={idx}>
                      {columns.map((column, index) => (
                        <TableCell key={`${index}-${Math.random()}`}>
                          {column.id !==
                            ('editIcon' ||
                              'viewIcon' ||
                              'editDeleteIcon' ||
                              'viewEditDeleteIcon' ||
                              'oneButton' ||
                              'twoButtons') && (
                            <Box className={setColor(index, row)}>
                              {column.format
                                ? column.format(row[column.id], row?.owner, row?.type, row?.riskScore, row)
                                : row[column.id]}
                            </Box>
                          )}

                          {column.id === 'viewIcon' && (
                            <Box textAlign={'center'}>
                              <IconButton className={classes.viewIcon} /* onClick={ () => onEdit(row.id) */>
                                <span className="material-icons">{icons.VIEW}</span>
                              </IconButton>
                            </Box>
                          )}

                          {column.id === 'editIcon' && (
                            <Box textAlign={'center'}>
                              <IconButton className={classes.editIcon} /* onClick={ () => onEdit(row.id) */>
                                <span className="material-icons">{icons.EDIT}</span>
                              </IconButton>
                            </Box>
                          )}

                          {column.id === 'editDeleteIcon' && (
                            <Box textAlign={'center'}>
                              <IconButton className={classes.editIcon} /* onClick={ () => onEdit(row.id) */>
                                <span className="material-icons">{icons.EDIT}</span>
                              </IconButton>
                              <IconButton className={classes.deleteIcon} /* onClick={ () => onEdit(row.id) */>
                                <span className="material-icons">{icons.DELETE}</span>
                              </IconButton>
                            </Box>
                          )}

                          {column.id === 'viewEditDeleteIcon' && (
                            <Box textAlign={'center'}>
                              <IconButton className={classes.viewIcon} /* onClick={ () => onEdit(row.id) */>
                                <span className="material-icons">{icons.VIEW}</span>
                              </IconButton>
                              <IconButton className={classes.editIcon} /* onClick={ () => onEdit(row.id) */>
                                <span className="material-icons">{icons.EDIT}</span>
                              </IconButton>
                              <IconButton className={classes.deleteIcon} /* onClick={ () => onEdit(row.id) */>
                                <span className="material-icons">{icons.DELETE}</span>
                              </IconButton>
                            </Box>
                          )}

                          {column.id === 'oneButton' && (
                            <Box className={classes.oneButton}>
                              <GreenButton
                                text={textButton || ''}
                                width={75}
                                height={24}
                                fontSize={12}
                                onClick={() => (getId ? getId(row.id) : null)}
                              />
                            </Box>
                          )}

                          {column.id === 'twoButtons' && (
                            <Box className={classes.twoButtons}>
                              <GreenButton
                                text={'Claim Yield'}
                                width={90}
                                height={24}
                                fontSize={12}
                                mr={12}
                                onClick={() => (getData ? getData(row) : null)}
                                disabled={row.yeild === '0'}
                              />

                              <GreenButton
                                text={'Unstake'}
                                width={75}
                                height={24}
                                fontSize={12}
                                onClick={() => (onUnstake ? onUnstake(row) : null)}
                                disabled={row.stake === '0'}
                              />
                            </Box>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })
              ) : (
                <TableRow className={classes.notFound}>
                  <TableCell colSpan={columns.length} align={'center'}>
                    {!loading && 'No results found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && data && data.length > 0 && totalFields && (
          <Grid className={classes.total}>
            <Grid className={classes.totalRow}>
              <Typography>Sub Total:</Typography>
              <Typography>{totalFields[0] /* .toFixed(3) */}</Typography>
            </Grid>

            <Grid className={classes.totalRow}>
              <Typography>Total Portfolio:</Typography>
              <Typography>{totalFields[1] /* .toFixed(3) */}</Typography>
            </Grid>
          </Grid>
        )}

        {viewAll && data && data.length > 0 && (
          <Grid container className={classes.viewAll}>
            <GrayButton text={'view all'} width={373} mt={8} mb={12} href={viewAllLink} />
          </Grid>
        )}

        {params && params.totalPages > 1 && (
          <Pagination
            className={classes.pagination}
            count={params.totalPages}
            defaultPage={params.currentPage || 1}
            shape="rounded"
            onChange={paginate}
            page={params.currentPage || 1}
          />
        )}
      </Paper>
    </>
  )
}
